import {
  Typography,
  Box,
  Stack,
  TextField,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { FormData } from "../../constants";
import { useEffect, useState } from "react";
import { IPayment } from "src/types/add-sales";
import moment from "moment";
import InfoBox from "src/shared/components/info-box";
import YoutubeViewerBox from "../../youtube-viewer-box";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utils";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const AggregatorDetails = ({
  control,
  errors,
  watch,
  index,
  journeyData,
  isOnboarding,
  journeyTaxPercentage,
  mode,
  aggsList,
  setValue,
}: {
  control: Control<FormData, any>;
  errors: FieldErrors<FormData>;
  watch: UseFormWatch<FormData>;
  index: number;
  journeyData: any;
  isOnboarding: boolean;
  journeyTaxPercentage: number;
  mode: "add" | "edit";
  aggsList: any[];
  setValue: UseFormSetValue<FormData>;
}) => {
  const [aggregatorSales, setAggregatorSales] = useState(0);
  const [aggregatorOrderQuantity, setAggregatorOrderQuantity] = useState(0);
  const journeyTaxDivider = 1 + journeyTaxPercentage;
  const journeyType = watch("journey_type");
  const { t } = useTranslation("addSales");
  const { t: generalT } = useTranslation("general");

  const findFeePercentage = (
    x: number,
    items: any[],
  ): { amount: number; type: "percentage" | "amount" } => {
    for (const item of items) {
      // Check if x lies in the inclusive range
      if (x >= item.from_amount && x <= item.to_amount) {
        return {
          amount: item.fee_percentage,
          type: item.is_percentage ? "percentage" : "amount",
        };
      }
    }
    return {
      amount: 0,
      type: "percentage",
    };
  };

  useEffect(() => {
    if (
      watch("journey_type") === "foodics" ||
      watch("journey_type") === "excel"
    ) {
      // Filter transactions based on target IDs
      const filteredPayments = journeyData?.payments.filter(
        (payment: IPayment) =>
          watch(`aggregators.${index}.payments`)?.includes(
            `${payment.paymentMethod.id}`,
          ),
      );
      // Calculate the accumulated total
      const accumulatedData = filteredPayments.reduce(
        (acc: { amount: number; quantity: number }, payment: IPayment) => {
          acc.amount += payment.total;
          acc.quantity += payment.quantity;
          return acc;
        },
        { amount: 0, quantity: 0 },
      );
      setAggregatorSales(accumulatedData.amount);
      setAggregatorOrderQuantity(accumulatedData.quantity);
    } else {
      setAggregatorSales(
        +(watch("aggregators")[index]?.net_sales_with_tax || 0),
      );
      setAggregatorOrderQuantity(
        +(watch("aggregators")[index]?.order_quantity || 0),
      );
    }
  }, [
    watch(`aggregators.${index}.payments`),
    watch("aggregators")[index]?.net_sales_with_tax,
    watch("aggregators")[index]?.order_quantity,
  ]);

  const startDate =
    journeyType === "foodics"
      ? journeyData?.start_date
      : watch("dateRange.start_date");
  const endDate =
    journeyType === "foodics"
      ? journeyData?.end_date
      : watch("dateRange.end_date");

  const otherAggsPaymentsIds: any[] = [];
  watch("aggregators").forEach((agg, i) => {
    if (i !== index) {
      otherAggsPaymentsIds.push(...(agg.payments as string[]));
    }
  });

  const availablePaymentsOptions = journeyData?.payments.filter(
    (pay: any) => !otherAggsPaymentsIds.includes(`${pay.paymentMethod.id}`),
  );

  const avgOrderValue = aggregatorSales / aggregatorOrderQuantity;

  useEffect(() => {
    if (!!watch("aggregators")[index].is_keta && journeyType === "manual") {
      const currentAgg = aggsList.find(
        (agg) => agg.id === Number(watch("aggregators")[index].aggregator_id),
      );
      const fee = findFeePercentage(
        avgOrderValue,
        currentAgg.aggregator_fees ?? [],
      );
      setValue(`aggregators.${index}.aggregator_fee`, `${fee.amount}`);
    }
  }, [aggregatorSales, aggregatorOrderQuantity]);

  useEffect(() => {
    if (!!watch("aggregators")[index].is_keta && journeyType === "manual") {
      const currentAgg = aggsList.find(
        (agg) => agg.id === Number(watch("aggregators")[index].aggregator_id),
      );
      const cost = findFeePercentage(
        avgOrderValue,
        currentAgg.aggregator_delivery_costs ?? [],
      );
      setValue(
        `aggregators.${index}.delivery_cost`,
        `${
          cost.type === "amount"
            ? cost.amount * aggregatorOrderQuantity
            : (cost.amount / 100) * aggregatorSales
        }`,
      );
    }
  }, [aggregatorSales, aggregatorOrderQuantity]);

  return (
    <div key={`_${index}`}>
      <Typography fontSize={"26px"} fontWeight={600}>
        {t("aggsDetails")}
      </Typography>
      <Typography color={"textSecondary"}>
        {t("fillTheRequiredInfo")}
      </Typography>
      <YoutubeViewerBox videoId="oyT2fpWX8vQ" />
      <form>
        <Stack gap={1.5} alignItems={"flex-start"}>
          {/* name */}
          <Controller
            name={`aggregators.${index}.aggregator_name`}
            control={control}
            render={({ field }) => (
              <TextField
                label={generalT("aggName")}
                {...field}
                error={!!errors.aggregators?.[index]?.aggregator_name}
                helperText={
                  errors.aggregators?.[index]?.aggregator_name?.message
                }
                fullWidth
                size="small"
                disabled
              />
            )}
          />
          {/* fee */}
          <Controller
            name={`aggregators.${index}.aggregator_fee`}
            control={control}
            render={({ field }) => (
              <TextField
                label={`${generalT("aggFee")} %`}
                type="number"
                {...field}
                error={!!errors.aggregators?.[index]?.aggregator_fee}
                helperText={
                  errors.aggregators?.[index]?.aggregator_fee?.message
                }
                fullWidth
                size="small"
                disabled={
                  mode === "edit" || !!watch("aggregators")[index].is_keta
                }
              />
            )}
          />
          {!isOnboarding && (
            <Stack direction={"row"} gap={1} flexWrap={"wrap"}>
              <Typography>{generalT("relatedPayments")}</Typography>
              {watch("aggregators")[index]?.payments_Names?.map((name) => {
                return (
                  <Chip
                    key={name}
                    label={name}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                );
              })}
            </Stack>
          )}

          {/* Duration */}
          <InfoBox
            text={`${t("aggValuesShould")}
              ${moment(startDate).format("MM-DD-YYYY")} ${
                startDate !== endDate
                  ? `${generalT("to")} ${moment(endDate).format("MM-DD-YYYY")}`
                  : ""
              } 
              `}
          />
          {/* For manual only */}
          {journeyType === "manual" && (
            <>
              <Controller
                name={`aggregators.${index}.net_sales_with_tax`}
                control={control}
                render={({ field }) => (
                  <TextField
                    label={t("salesIncludeTax")}
                    type="number"
                    {...field}
                    error={!!errors.aggregators?.[index]?.net_sales_with_tax}
                    helperText={
                      errors.aggregators?.[index]?.net_sales_with_tax?.message
                    }
                    fullWidth
                    size="small"
                    disabled={!!watch("aggregators")[index].has_afc}
                  />
                )}
              />
              <Controller
                name={`aggregators.${index}.order_quantity`}
                control={control}
                render={({ field }) => (
                  <TextField
                    label={generalT("orderQuantity")}
                    type="number"
                    {...field}
                    error={!!errors.aggregators?.[index]?.order_quantity}
                    helperText={
                      errors.aggregators?.[index]?.order_quantity?.message
                    }
                    fullWidth
                    size="small"
                    disabled={!!watch("aggregators")[index].has_afc}
                  />
                )}
              />
            </>
          )}
          {!watch("aggregators")[index]?.is_keta ? (
            <>
              {/* Type of Delivery */}
              <Stack
                direction={"row"}
                alignItems={"center"}
                gap={{ xs: 1, sm: 1, md: 3 }}
                flexWrap={"wrap"}
                width={"100%"}
              >
                <Typography color={"textSecondary"}>
                  {t("addAggOffer")}
                </Typography>
                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors.has_aggregators}
                  sx={{ width: "fit-content", ml: { xs: "auto", sm: "unset" } }}
                >
                  <Controller
                    name={`aggregators.${index}.delivery_type`}
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        {...field}
                        row
                        aria-labelledby="demo-row-radio-buttons-group-label"
                        name="row-radio-buttons-group"
                      >
                        <FormControlLabel
                          value="order"
                          control={
                            <Radio
                              icon={<CircleOutlinedIcon />}
                              checkedIcon={<CheckCircleIcon />}
                            />
                          }
                          label={t("costByOrder")}
                          sx={{ color: "#98A2B3" }}
                          disabled={!!watch("aggregators")[index].has_afc}
                        />
                        <FormControlLabel
                          value="amount"
                          control={
                            <Radio
                              icon={<CircleOutlinedIcon />}
                              checkedIcon={<CheckCircleIcon />}
                            />
                          }
                          label={t("costByAmount")}
                          sx={{ color: "#98A2B3" }}
                          disabled={!!watch("aggregators")[index].has_afc}
                        />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Stack>
              <FormHelperText error id="my-helper-text" sx={{ mt: -3, px: 2 }}>
                {errors.aggregators &&
                  !!errors.aggregators[index]?.delivery_type}
              </FormHelperText>
              {/* Delivery Cost */}
              <Stack
                direction={"row"}
                gap={"20px"}
                width={"100%"}
                alignItems={"flex-start"}
                flexWrap={{ xs: "wrap", sm: "nowrap" }}
              >
                <Controller
                  name={`aggregators.${index}.delivery_cost`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      label={
                        watch(`aggregators.${index}.delivery_type`) === "order"
                          ? t("orderCostIncludeTax")
                          : t("amountIncludeTax")
                      }
                      {...field}
                      error={!!errors.aggregators?.[index]?.delivery_cost}
                      helperText={
                        errors.aggregators?.[index]?.delivery_cost?.message
                      }
                      fullWidth
                      size="small"
                      sx={{ flexBasis: { xs: "100%", sm: "60%" } }}
                      disabled={
                        !!watch("aggregators")[index].is_keta ||
                        !!watch("aggregators")[index].has_afc
                      }
                    />
                  )}
                />
                <Stack
                  flexBasis={{ xs: "100%", sm: "40%" }}
                  width={"100%"}
                  bgcolor={"#FFF3EA"}
                  borderRadius={"4px"}
                  padding={1}
                  direction={"row"}
                  gap={"10px"}
                >
                  <InfoOutlinedIcon color="primary" />
                  <Typography>
                    {watch(`aggregators.${index}.delivery_type`) === "order"
                      ? ` ${t("oneOrderCost")} ( ${formatNumber(
                          Number(watch(`aggregators.${index}.delivery_cost`)),
                        )} )`
                      : generalT("deliveryCostWithTax")}
                  </Typography>
                </Stack>
              </Stack>
            </>
          ) : null}

          {/* Calculations */}
          <Divider variant="fullWidth" sx={{ width: "100%" }} />
          <Box width={"100%"}>
            <Box height={"7px"} bgcolor={"#D0D5DD"} />
            <Stack p={2} gap={1.5} bgcolor={"#F9FAFB"}>
              <Stack direction={"row"} justifyContent={"space-between"}>
                <Typography fontSize={"14px"} color={"#344054"}>
                  {t("salesFromAgg")}
                </Typography>
                <Typography fontSize={"14px"} color={"#344054"}>
                  {aggregatorSales ? formatNumber(aggregatorSales) : "--"}{" "}
                  {generalT("sar")}
                </Typography>
              </Stack>
              <Stack direction={"row"} justifyContent={"space-between"}>
                <Typography fontSize={"14px"} color={"#344054"}>
                  {t("salesTax")}
                </Typography>
                <Typography fontSize={"14px"} color={"#344054"}>
                  {aggregatorSales
                    ? formatNumber(
                        +(
                          (+aggregatorSales / journeyTaxDivider) *
                          journeyTaxPercentage
                        ).toFixed(2),
                      )
                    : "--"}{" "}
                  {generalT("sar")}
                </Typography>
              </Stack>
              <Stack direction={"row"} justifyContent={"space-between"}>
                <Typography fontSize={"14px"} color={"#344054"}>
                  {generalT("aggFee")}:
                </Typography>
                <Typography fontSize={"14px"} color={"#344054"}>
                  {watch(`aggregators.${index}.aggregator_fee`)
                    ? formatNumber(
                        +(
                          (+aggregatorSales / journeyTaxDivider) *
                          (+watch(`aggregators.${index}.aggregator_fee`) / 100)
                        ).toFixed(2),
                      )
                    : "--"}{" "}
                  {generalT("sar")}
                </Typography>
              </Stack>
              <Stack direction={"row"} justifyContent={"space-between"}>
                <Typography fontSize={"14px"} color={"#344054"}>
                  {t("aggFeeTax")}
                </Typography>
                <Typography fontSize={"14px"} color={"#344054"}>
                  {watch(`aggregators.${index}.aggregator_fee`)
                    ? formatNumber(
                        +(
                          (+aggregatorSales / journeyTaxDivider) *
                          (+watch(`aggregators.${index}.aggregator_fee`) /
                            100) *
                          journeyTaxPercentage
                        ).toFixed(2),
                      )
                    : "--"}{" "}
                  {generalT("sar")}
                </Typography>
              </Stack>
              <Stack direction={"row"} justifyContent={"space-between"}>
                <Typography fontSize={"14px"} color={"#344054"}>
                  {generalT("orderQnty")}:
                </Typography>
                <Typography fontSize={"14px"} color={"#344054"}>
                  {aggregatorOrderQuantity
                    ? formatNumber(aggregatorOrderQuantity)
                    : "--"}
                </Typography>
              </Stack>
              <Stack direction={"row"} justifyContent={"space-between"}>
                <Typography fontSize={"14px"} color={"#344054"}>
                  {generalT("deliveryCostWithTax")}:
                </Typography>
                <Typography fontSize={"14px"} color={"#344054"}>
                  {watch(`aggregators.${index}.delivery_type`) === "order"
                    ? formatNumber(
                        +(
                          +aggregatorOrderQuantity *
                          Number(
                            watch(`aggregators.${index}.delivery_cost`) ?? 0,
                          )
                        ).toFixed(2),
                      )
                    : formatNumber(
                        +Number(
                          watch(`aggregators.${index}.delivery_cost`) ?? 0,
                        ).toFixed(2),
                      )}{" "}
                  {generalT("sar")}
                </Typography>
              </Stack>
              <Stack direction={"row"} justifyContent={"space-between"}>
                <Typography fontSize={"14px"} color={"#344054"}>
                  {t("deliveryCostTax")}
                </Typography>
                <Typography fontSize={"14px"} color={"#344054"}>
                  {watch(`aggregators.${index}.delivery_type`) === "order"
                    ? formatNumber(
                        +(
                          ((+aggregatorOrderQuantity *
                            Number(
                              watch(`aggregators.${index}.delivery_cost`),
                            ) || 0) /
                            journeyTaxDivider) *
                          journeyTaxPercentage
                        ).toFixed(2),
                      )
                    : formatNumber(
                        +(
                          (Number(
                            watch(`aggregators.${index}.delivery_cost`) ?? 0,
                          ) /
                            journeyTaxDivider) *
                          journeyTaxPercentage
                        ).toFixed(2),
                      )}{" "}
                  {generalT("sar")}
                </Typography>
              </Stack>
            </Stack>
          </Box>
          <Accordion variant="elevation" elevation={1}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <InfoOutlinedIcon fontSize="small" color="primary" />
              <Typography>
                {t("estimatedAggCommissionNotFinal")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography fontSize={"14px"}>
                {t("aggCommissionEstimatedDesc")}
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </form>
    </div>
  );
};

export default AggregatorDetails;
